// i18n should be imported first
import '../i18n'
import '../index.css'
import '@egw/design-system/index.css'
import { useEffect, type FC } from 'react'
import { analyticsService } from '@egw/analytics'
import { QueryClientProvider } from '@egw/api'
import { inAppChatService } from '@egw/app-chat'
import { AuthProvider, useAuth, passkeyService } from '@egw/auth'
import { configClient } from '@egw/config'
import { featureFlags } from '@egw/constants'
import { initGraphql, http } from '@egw/clients'
import { deepLinkingService } from '@egw/deep-linking'
import { deviceService } from '@egw/device'
import { eventsService } from '@egw/events'
import { platformServiceInstance as platformService } from '@egw/platform-service'
import { riskService } from '@egw/risk-service'
import { FeatureFlagsProvider } from '@egw/feature-flags'
import { screenRecordingService } from '@egw/screen-recording'
import { WealthPreloadImages } from '@egw/assets'
import { liveUpdateService } from '@egw/live-update'
import { ForceUpdate } from '@egw/features-system'
import { AiChatProvider } from '@egw/ai-chat'
import { Router } from '../pages/router'
import { config } from '../config'

/**
 * Initialize services which do not depend on the auth state
 */

// Set application configuration
configClient.setConfig(config)

// Initialize Risk Service
riskService.init(config)

// Initialize GraphQL client
initGraphql(config.graphql.url)

// Initialize HTTP client
if (config.http) {
  http.init(config.http)
}

// Events Service
eventsService.init()

// Platform Service
platformService.init(config)

// Passkey Service
void passkeyService.init()

// Device Service - FingerprintJS
void deviceService.initDeviceService(config)

// In-App Chat Service - Zendesk
inAppChatService.init(config)

// Analytics Service - RudderStack
// Braze, FullStory, Google Tag Manager are initialized via Rudderstack
if (config.rudderstack) {
  analyticsService.init({
    rudderstack: config.rudderstack,
    braze: config.braze,
    fullStory: config.fullStory,
    gtm: config.gtm,
  })
}

// Live Update Service
liveUpdateService.init(config)

// Deep Linking Service
deepLinkingService.init()

// Consumer Engagement Service - Braze via Rudderstack
// Rudderstack handles the initialization of Braze
// Service handles consumer engagement features

// Monitoring Service (Sentry) has to be initialized before the mounting React component.
// That's why the Sentry initialize is in the main.tsx file.

/**
 * Initialize services which depend on the react state
 */
const AppServices: FC = () => {
  const { isLoading } = useAuth()

  useEffect(() => {
    if (!isLoading) {
      // Initialize services that depend on the auth state
    }
  }, [isLoading])
  return (
    <>
      <ForceUpdate />
      {/* Other components and services */}
    </>
  )
}

export const App: FC = () => {
  return (
    <QueryClientProvider>
      <FeatureFlagsProvider config={config} defaultFlags={featureFlags}>
        <AuthProvider>
          <AiChatProvider config={config.aiChat}>
            <WealthPreloadImages />
            <AppServices />
            <Router />
          </AiChatProvider>
        </AuthProvider>
      </FeatureFlagsProvider>
    </QueryClientProvider>
  )
}
